/**
 * alert-beacon-logic — the pure counting behind the map's alert beacon.
 *
 * WHY A UTIL: the component injects nothing but is mounted inside the shell,
 * so it is awkward to exercise directly. Everything that DECIDES — which
 * buckets exist, which are worth a segment, which face the pill wears —
 * lives here as pure functions over plain records.
 *
 * THE PATTERN IT FOLLOWS. Manage's estate map (`estate-map-page.component`)
 * states its counts as a `·`-separated run of segments and DROPS every
 * bucket whose count is zero: "a facet with no rows behind it is not a
 * filter, it is a claim that there is nothing to find". The same rule holds
 * here for a different reason — a beacon reading "2 closed · 0 urgent ·
 * 0 advisory" spends three segments to say one thing, on a surface whose
 * whole point is not spending space.
 *
 * WHAT IT DELIBERATELY DOES NOT COPY. Manage's counts are FILTER CHIPS:
 * clicking one narrows the plan and the side list. These are a readout. The
 * employee map has no per-severity dimming to drive and no side list to
 * narrow, so a chip that looked pressable and only opened the same popover
 * as its neighbour would be an affordance that lies.
 */
import type { BuildingAlert } from '../../../../core/services/building-alerts.service';

/**
 * The buckets, in the order they read. `closed` first because a closure is
 * the only one of the three that changes whether you can book.
 */
export type AlertBeaconSegmentKey = 'closed' | 'urgent' | 'notice';

export interface AlertBeaconSegment {
  readonly key: AlertBeaconSegmentKey;
  readonly count: number;
  /** Transloco key for the trailing word. Counts are rendered separately so
   *  the number can carry `font-variant-numeric: tabular-nums` on its own. */
  readonly labelKey: string;
}

const LABEL_KEYS: Readonly<Record<AlertBeaconSegmentKey, string>> = {
  closed: 'shell.alert_beacon.seg_closed',
  urgent: 'shell.alert_beacon.seg_urgent',
  notice: 'shell.alert_beacon.seg_notice',
};

/**
 * Which bucket one alert falls in.
 *
 * `blockBookings` outranks severity, and that ordering is the whole point:
 * an info-severity closure still takes the room out of service, so it is
 * counted as closed rather than filed under a word that implies it is only
 * worth knowing about. Mirrors `ClosuresService`' definition of a closure —
 * one record type, two kinds, `blockBookings` is the split.
 */
export function bucketFor(alert: BuildingAlert): AlertBeaconSegmentKey {
  if (alert.blockBookings) return 'closed';
  if (alert.severity === 'urgent') return 'urgent';
  return 'notice';
}

/** How many of `alerts` take spaces out of service. */
export function blockingCount(alerts: readonly BuildingAlert[]): number {
  return alerts.filter((a) => a.blockBookings === true).length;
}

/**
 * The pill's segments — non-empty buckets only, in `closed → urgent →
 * notice` order. An empty input yields an empty array; the component then
 * renders nothing, so callers need no guard.
 */
export function beaconSegments(
  alerts: readonly BuildingAlert[],
): AlertBeaconSegment[] {
  const tally: Record<AlertBeaconSegmentKey, number> = {
    closed: 0,
    urgent: 0,
    notice: 0,
  };
  for (const alert of alerts) tally[bucketFor(alert)] += 1;

  const order: AlertBeaconSegmentKey[] = ['closed', 'urgent', 'notice'];
  return order
    .filter((key) => tally[key] > 0)
    .map((key) => ({ key, count: tally[key], labelKey: LABEL_KEYS[key] }));
}

/**
 * The pill's face.
 *
 * `error` whenever anything is closed or urgent — i.e. whenever the set
 * contains something that changes what the viewer can do. That is the
 * compaction's one hard constraint: smaller must not mean quieter, so the
 * beacon takes the loudest semantic tone its contents justify rather than
 * the average of them.
 */
export function beaconTone(
  alerts: readonly BuildingAlert[],
): 'error' | 'warning' | 'info' {
  if (alerts.some((a) => a.blockBookings || a.severity === 'urgent')) {
    return 'error';
  }
  if (alerts.some((a) => a.severity === 'warning')) return 'warning';
  return 'info';
}
